import { useState, useEffect } from "react";
import { inviteAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { UserPlus, Send, Mail, Copy, Trash2, Check, X, Users, Loader2, ClipboardPaste, RefreshCw } from "lucide-react";

const FRONTEND_URL = window.location.origin;

export const InviteModal = ({ open, onOpenChange, boardId, boardName, gameMode, scheduledStart, scheduledEnd, durationMinutes }) => {
  const [tab, setTab] = useState("invite");
  const [invitees, setInvitees] = useState([{ email: "", name: "" }]);
  const [customMessage, setCustomMessage] = useState(
    `You've been invited to participate in the Unstoppable Company Game for "${boardName}". Together we'll map our business workflows and make strategic AI decisions.\n\nPlease click the link below to join the session and start making your decisions.`
  );
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");

  useEffect(() => {
    if (open && boardId) {
      fetchParticipants();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, boardId]);

  const fetchParticipants = async () => {
    setLoadingParticipants(true);
    try {
      const res = await inviteAPI.getParticipants(boardId);
      setParticipants(res.data.participants || []);
    } catch {
      // silent
    } finally {
      setLoadingParticipants(false);
    }
  };

  const parseBulkEmails = (text) => {
    // Split by commas, semicolons, newlines, tabs, or spaces
    const raw = text.split(/[,;\n\t]+/).map((s) => s.trim()).filter(Boolean);
    const parsed = [];
    for (const entry of raw) {
      // Handle "Name <email>" format
      const angleMatch = entry.match(/^(.+?)\s*<([^>]+)>$/);
      if (angleMatch) {
        parsed.push({ name: angleMatch[1].trim(), email: angleMatch[2].trim().toLowerCase() });
        continue;
      }
      // Handle "name email" with space (if second part looks like email)
      const parts = entry.split(/\s+/);
      if (parts.length >= 2) {
        const lastPart = parts[parts.length - 1];
        if (lastPart.includes("@")) {
          parsed.push({ name: parts.slice(0, -1).join(" "), email: lastPart.toLowerCase() });
          continue;
        }
      }
      // Plain email
      if (entry.includes("@")) {
        parsed.push({ name: "", email: entry.toLowerCase() });
      }
    }
    return parsed;
  };

  const applyBulkEmails = () => {
    const parsed = parseBulkEmails(bulkText);
    if (parsed.length === 0) {
      toast.error("No valid emails found. Paste emails separated by commas, newlines, or semicolons.");
      return;
    }
    // Merge with existing (skip duplicates)
    const existingEmails = new Set(invitees.map((i) => i.email.toLowerCase()).filter(Boolean));
    const newEntries = parsed.filter((p) => !existingEmails.has(p.email));
    const cleanExisting = invitees.filter((i) => i.email.trim());
    setInvitees(cleanExisting.length > 0 ? [...cleanExisting, ...newEntries] : newEntries.length > 0 ? newEntries : [{ email: "", name: "" }]);
    setBulkText("");
    setBulkMode(false);
    toast.success(`${newEntries.length} email(s) added${parsed.length - newEntries.length > 0 ? ` (${parsed.length - newEntries.length} duplicates skipped)` : ""}`);
  };

  const addRow = () => setInvitees([...invitees, { email: "", name: "" }]);

  const removeRow = (idx) => {
    if (invitees.length === 1) return;
    setInvitees(invitees.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, val) => {
    setInvitees(invitees.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const sendInvites = async () => {
    const valid = invitees.filter((i) => i.email.trim());
    if (valid.length === 0) {
      toast.error("Add at least one email");
      return;
    }
    setSending(true);
    try {
      const res = await inviteAPI.send(boardId, {
        invitees: valid,
        custom_message: customMessage,
      });
      setResults(res.data.results || []);
      toast.success(`${res.data.total_invited} participant(s) invited`);
      fetchParticipants();
      setTab("results");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites");
    } finally {
      setSending(false);
    }
  };

  const removeParticipant = async (userId) => {
    try {
      await inviteAPI.removeParticipant(boardId, userId);
      setParticipants((prev) => prev.filter((p) => p.user_id !== userId));
      toast.success("Participant removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${FRONTEND_URL}/invite/${token}`);
    setCopiedToken(token);
    toast.success("Invite link copied!");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const formatGameInfo = () => {
    if (gameMode === "live") {
      return scheduledStart
        ? `Live Session: ${new Date(scheduledStart).toLocaleString()}`
        : "Live Session (time TBD)";
    }
    const parts = [];
    if (scheduledStart) parts.push(`Starts: ${new Date(scheduledStart).toLocaleString()}`);
    if (scheduledEnd) parts.push(`Deadline: ${new Date(scheduledEnd).toLocaleString()}`);
    if (durationMinutes) parts.push(`Duration: ${durationMinutes} minutes`);
    return parts.length > 0 ? `Async Game - ${parts.join(" | ")}` : "Async Game";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-500" />
            Invite Participants
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="invite" data-testid="invite-tab">Invite</TabsTrigger>
            <TabsTrigger value="preview" data-testid="preview-tab">Email Preview</TabsTrigger>
            <TabsTrigger value="results" data-testid="participants-tab">
              Participants ({participants.length})
            </TabsTrigger>
          </TabsList>

          {/* Invite Tab */}
          <TabsContent value="invite" className="space-y-4 mt-4">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
              <span className="text-xs font-semibold text-indigo-700">{formatGameInfo()}</span>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium text-slate-700">Participants</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="toggle-bulk-mode-btn"
                  onClick={() => setBulkMode(!bulkMode)}
                  className="h-7 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                >
                  <ClipboardPaste className="w-3.5 h-3.5 mr-1" />
                  {bulkMode ? "Switch to Individual" : "Bulk Add Emails"}
                </Button>
              </div>

              {bulkMode ? (
                <div className="space-y-3 animate-fade-in">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <Textarea
                      data-testid="bulk-email-textarea"
                      placeholder={"Paste emails here — one per line, or separated by commas/semicolons.\n\nSupported formats:\njohn@company.com\nJane Smith <jane@company.com>\njohn@example.com, jane@example.com\nbob@team.com; alice@team.com"}
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      className="min-h-[140px] bg-white border-slate-200 text-sm font-mono resize-none"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[11px] text-slate-400">
                        {bulkText.trim() ? `${parseBulkEmails(bulkText).length} email(s) detected` : "Paste or type email addresses"}
                      </span>
                      <Button
                        data-testid="apply-bulk-btn"
                        onClick={applyBulkEmails}
                        disabled={!bulkText.trim()}
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" />
                        Add to List
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {invitees.map((row, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          data-testid={`invite-name-${idx}`}
                          placeholder="Name"
                          value={row.name}
                          onChange={(e) => updateRow(idx, "name", e.target.value)}
                          className="h-9 bg-white flex-1"
                        />
                        <Input
                          data-testid={`invite-email-${idx}`}
                          placeholder="email@company.com"
                          type="email"
                          value={row.email}
                          onChange={(e) => updateRow(idx, "email", e.target.value)}
                          className="h-9 bg-white flex-[2]"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(idx)}
                          disabled={invitees.length === 1}
                          className="h-9 w-9 p-0 text-slate-400 hover:text-red-500 shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="add-invitee-btn"
                    onClick={addRow}
                    className="mt-2 text-xs text-indigo-600 hover:text-indigo-700"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1" />
                    Add Another
                  </Button>
                </>
              )}
            </div>

            {/* Show count of queued invitees */}
            {invitees.filter((i) => i.email.trim()).length > 0 && !bulkMode && (
              <div className="bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  {invitees.filter((i) => i.email.trim()).length} participant(s) ready to invite
                </span>
                {invitees.filter((i) => i.email.trim()).length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInvitees([{ email: "", name: "" }])}
                    className="h-6 text-[10px] text-slate-400 hover:text-red-500"
                  >
                    Clear All
                  </Button>
                )}
              </div>
            )}

            <Button
              data-testid="send-invites-btn"
              onClick={sendInvites}
              disabled={sending || invitees.filter((i) => i.email.trim()).length === 0}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-10 active:scale-[0.98]"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending Invites...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send Invites ({invitees.filter((i) => i.email.trim()).length})</>
              )}
            </Button>
          </TabsContent>

          {/* Email Preview Tab */}
          <TabsContent value="preview" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-900 px-6 py-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-indigo-500 rounded-md flex items-center justify-center">
                    <span className="text-white font-heading font-bold text-xs">U</span>
                  </div>
                  <span className="text-white font-heading font-semibold text-sm">Unstoppable Company Game</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Mail className="w-3.5 h-3.5" />
                  <span>To: participant@company.com</span>
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900">
                  You're Invited to the Unstoppable Company Game
                </h3>
                <Textarea
                  data-testid="email-message-textarea"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="min-h-[120px] text-sm border-slate-200 bg-slate-50"
                />
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 space-y-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-indigo-500">Session Details</div>
                  <div className="text-sm text-slate-700 font-medium">{boardName}</div>
                  <div className="text-xs text-slate-500">{formatGameInfo()}</div>
                </div>
                <div className="bg-indigo-600 text-white text-center py-2.5 rounded-lg text-sm font-medium">
                  Join the Game
                </div>
                <p className="text-[11px] text-slate-400 text-center">
                  Email sending is a placeholder - invite links will be generated for now.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Participants/Results Tab */}
          <TabsContent value="results" className="mt-4 space-y-4">
            {results.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Latest Invite Results
                </Label>
                {results.map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
                    <div>
                      <span className="text-sm font-medium text-slate-700">{r.name || r.email}</span>
                      <span className="text-xs text-slate-400 ml-2">{r.email}</span>
                      {r.is_new_user && (
                        <Badge variant="outline" className="ml-2 text-[10px] text-indigo-600 border-indigo-200">
                          New Account
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {r.invite_token && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`copy-invite-link-${i}`}
                          onClick={() => copyLink(r.invite_token)}
                          className="h-7 text-xs"
                        >
                          {copiedToken === r.invite_token ? (
                            <><Check className="w-3 h-3 mr-1 text-emerald-500" />Copied</>
                          ) : (
                            <><Copy className="w-3 h-3 mr-1" />Copy Link</>
                          )}
                        </Button>
                      )}
                      <Badge className={`text-[10px] ${
                        r.status === "invited" ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-amber-50 text-amber-600 border-amber-200"
                      }`}>
                        {r.status === "already_invited" ? "Already Invited" : "Invited"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                All Participants ({participants.length})
              </Label>
              {loadingParticipants ? (
                <div className="text-sm text-slate-400 py-4 text-center">Loading...</div>
              ) : (
                participants.map((p, i) => (
                  <div key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600">
                        {(p.name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-700">{p.name}</span>
                        <span className="text-xs text-slate-400 ml-2">{p.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {/* Copy invite link */}
                      {p.invite_token && p.role !== "owner" && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`copy-participant-link-${i}`}
                          onClick={() => copyLink(p.invite_token)}
                          className="h-7 text-xs"
                        >
                          {copiedToken === p.invite_token ? (
                            <><Check className="w-3 h-3 mr-1 text-emerald-500" />Copied</>
                          ) : (
                            <><Copy className="w-3 h-3 mr-1" />Copy Link</>
                          )}
                        </Button>
                      )}
                      {/* Resend invite */}
                      {p.role !== "owner" && p.status !== "active" && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`resend-invite-${i}`}
                          onClick={async () => {
                            try {
                              await inviteAPI.send(boardId, { invitees: [{ email: p.email, name: p.name }], custom_message: customMessage });
                              toast.success(`Invite resent to ${p.email}`);
                            } catch { toast.error("Failed to resend"); }
                          }}
                          className="h-7 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />Resend
                        </Button>
                      )}
                      <Badge className={`text-[10px] ${
                        p.role === "owner" ? "bg-indigo-50 text-indigo-600 border-indigo-200" :
                        p.status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                        "bg-amber-50 text-amber-600 border-amber-200"
                      }`}>
                        {p.role === "owner" ? "Owner" : p.status === "active" ? "Joined" : "Pending"}
                      </Badge>
                      {p.role !== "owner" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`remove-participant-${i}`}
                          onClick={() => removeParticipant(p.user_id)}
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default InviteModal;
